import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Input, Select } from 'antd';
// import { useWarehouseStore } from 'store/warehouseStore';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from '../graphql/queries/getAllCarrierOptions';
import { GET_HUB_LIST, GetHubListResponse } from '../graphql/queries/getHubList';
import { GET_WAREHOUSE_LIST_BY_HUB_ID, GetWarehouseListByHubIdResponse } from '../graphql/queries/getWarehouseListByHubId';
import { GET_AREAS_BY_WAREHOUSE_ID, GetAreasByWarehouseIdResponse } from '../../../../graphql/queries/getAreasByWarehouseId';
import { GET_EXTERNAL_STATION_SORTING_CODES, GetExternalStationSortingCodesResponse } from '../graphql/queries/getExternalStationSortingCodes';
import { GET_TIKTOK_SORT_CENTER_MAPPINGS, GetTikTokSortCenterMappingsResponse } from '../graphql/queries/getTikTokSortCenterMappings';
import { GET_TT_LOCATION_OPTIONS, GetTtLocationOptionsResponse, TT_LOCATION_TYPE } from '../graphql/queries/getTtLocationOptions';
import {
    LIST_ACTIVE_BAG_CONTAINERS,
    ListActiveBagContainersResponse,
    BagContainerOperateType,
} from '../graphql/queries/listActiveBagContainers';
import { ExternalBusinessType } from '../graphql/types/BagTagType';
import { ttBaggingSortingRuleCarrierSegment } from '../utils/carrierUtils';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';

export type FilterOptions = {
    bagNumber: string;
    externalBagNumber: string;
    bagType: string | 'all';
    warehouseId: string | 'all';
    sortingCode: string;
    status: string | 'all';
    createdAtRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
    bagTaskNumber: string; // 组包任务号
    carrierCode?: number; // 第三方末端承运商
    sourceType?: number | 'all'; // 来源类型：0-手动创建，1-组包创建，2-交叉带创建，3-批次创建，4-客户大包扫描，5-直线分拣机创建
    batchNumber?: string; // 批次号
    mergedToBagNumber?: string; // 合包归属包牌号（完整匹配）
    sortingRuleSecond?: string; // 预分拣码（部分匹配）
    // 分拣码分段筛选：前 3 段级联下拉，第 4 段自由输入；后端拼接为完整前缀走 LIKE 'X%'
    sortingCodeSegment1?: string;
    // seg1 下拉的选中项标识 "来源::字面量"（仅前端用，不交后端）——见 buildSeg1Key 注释
    sortingCodeSegment1Key?: string;
    sortingCodeSegment2?: string;
    sortingCodeSegment3?: string;
    sortingCodeSegment4?: string;
    // PPMOD 新增：外部业务类型（0=普通 / 1=PP，undefined=不筛选）+ 容器类型 id
    externalBusinessType?: number;
    bagContainerId?: number;
};

// 修改为函数，支持国际化
export const getBagTypeOptions = (t: any) => [
    { value: 'all', label: t('wms.bags.options.all') },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
];

// 来源类型选项
export const getSourceTypeOptions = (t: any) => [
    { value: 'all', label: t('wms.bags.options.all') },
    { value: 0, label: t('wms.bags.sourceType.system') || '手动创建' },
    { value: 1, label: t('wms.bags.sourceType.bagTask') || '组包创建' },
    { value: 2, label: t('wms.bags.sourceType.crossBelt') || '交叉带创建' },
    { value: 3, label: t('wms.bags.sourceType.batch') || '批次创建' },
    { value: 4, label: t('wms.bags.sourceType.clientCarton') || '客户大包扫描' },
    { value: 5, label: t('wms.bags.sourceType.linearSorter') || '直线分拣机创建' },
];

export const getBagStatusOptions = (t: any) => [
    { value: 'all', label: t('wms.bags.options.all') },
    { value: '0', label: t('wms.bags.status.unused') },
    { value: '1', label: t('wms.bags.status.used') },
    { value: '2', label: t('wms.bags.status.discarded') },
    { value: '3', label: t('enum.bagStatus.handover') },
    { value: '4', label: t('enum.bagStatus.closed') },
];

// 保持向后兼容
export const bagTypeOptions = getBagTypeOptions(() => 'All');
export const bagStatusOptions = getBagStatusOptions(() => 'All');

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    bagNumber: '',
    externalBagNumber: '',
    bagType: 'all',
    warehouseId: 'all',
    sortingCode: '',
    status: 'all',
    createdAtRange: [dayjs().subtract(7, 'day'), dayjs()],
    bagTaskNumber: '',
    sourceType: 'all',
    batchNumber: '',
    mergedToBagNumber: '',
    sortingRuleSecond: '',
    sortingCodeSegment1: undefined,
    sortingCodeSegment1Key: undefined,
    sortingCodeSegment2: undefined,
    sortingCodeSegment3: undefined,
    sortingCodeSegment4: undefined,
    externalBusinessType: undefined,
    bagContainerId: undefined,
};

interface FilterAreaProps {
    filters: FilterOptions;
    setFilters: (filters: FilterOptions) => void;
}

// TEMU 分拣码 seg1 硬编码：与后端 ExternalStationType 枚举对齐（1=TEMU_GA, 2=TEMU_SCF），
// value 就是 TEMU 分拣码首段字面量（GA / SCF），额外记 stationType 用于拉 seg3 候选。
// 混合在 seg1 下拉里与 SWX Hub 并列展示。
const TEMU_SEG1_OPTIONS: Array<{ label: string; value: string; stationType: number }> = [
    { label: 'GA', value: 'GA', stationType: 1 },
    { label: 'SCF', value: 'SCF', stationType: 2 },
    // DHLe：单段分拣码，seg1 即目的地 "LA"（stationType=3），无 seg2/seg3
    { label: 'LA', value: 'LA', stationType: 3 },
];

// TEMU 分拣码 seg2 硬编码（业务确认：仅 H / G 两个值）
const TEMU_SEG2_OPTIONS: Array<{ label: string; value: string }> = [
    { label: 'H', value: 'H' },
    { label: 'G', value: 'G' },
];

// 后端 FinalMileCarrier.USPS 的枚举值（api-server 的 dto/FinalMileCarrier.kt: USPS(1)）。
//
// ⚠️ 用途仅限「未选尾程商时给 TEMU seg3 拉候选」，**不参与筛选条件**：
// 筛选提交的是 sortingCodeSegment1/2/3 三个字面量（后端 BagListingTypes），按 bag.sorting_code 匹配，
// 与 carrier 无关；而 getExternalStationSortingCodes 的 gql 契约要求 carrierCode 必填，
// 未选尾程商时若不给值只能 skip 查询，seg3 下拉就会空着且界面无任何提示。
// 因此这里只兜候选来源。**切勿把它写进 defaultFilters.carrierCode** ——
// 那会让筛选结果被静默限制为 USPS（用户看不出来），且波及 hub / TT 分支。
const USPS_CARRIER_CODE = 1;

// seg1 的三个候选源（SWX Hub / TT sortCenterCode / TEMU）存在"同字面量不同语义"的分拣码，
// 例：LAX HUB 的 sortingCode "LA" 与 TEMU DHLe 目的地码 "LA" 并非同一含义，两者都要展示。
// 若直接把字面量当 Select 的 value，antd 会把 options 摊成 value→option 反查回显 label，
// 同 value 后者覆盖前者 ⇒ 选中 "LA - LAX HUB" 却回显 "LA (TEMU)"，且无法区分用户选的是哪一个。
// 故下拉 value 用 "来源::字面量" 复合 key（存 sortingCodeSegment1Key，仅前端用），
// 交后端的 sortingCodeSegment1 仍是纯字面量。分拣码字面量为段码（字母数字），不含 "::"。
type Seg1Source = 'hub' | 'tt' | 'temu';
const SEG1_KEY_SEP = '::';
const buildSeg1Key = (source: Seg1Source, value: string) => `${source}${SEG1_KEY_SEP}${value}`;
const parseSeg1Key = (key: string | undefined): { source: Seg1Source; value: string } | null => {
    if (!key) return null;
    const sepIndex = key.indexOf(SEG1_KEY_SEP);
    if (sepIndex <= 0) return null;
    const value = key.slice(sepIndex + SEG1_KEY_SEP.length);
    if (!value) return null;
    return { source: key.slice(0, sepIndex) as Seg1Source, value };
};

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {

    // 将 Dayjs 数组转换为日期字符串数组，用于 DateRangeFilterItem
    const getCreatedAtRangeString = (range: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null): [string | null, string | null] | null => {
        if (!range) return null;
        return [
            range[0] ? range[0].format('YYYY/MM/DD') : null,
            range[1] ? range[1].format('YYYY/MM/DD') : null
        ];
    };

    const currentFilters = filters || DEFAULT_FILTER_OPTIONS;
    const createdAtRange = getCreatedAtRangeString(filters.createdAtRange);

    const { t } = useTranslation();

    const labelBagNumber = String(t('wms.bags.filters.bagNumber'));
    const labelExternalBagNumber = String(t('wms.bags.filters.externalBagNumber'));
    const labelTagType = String(t('wms.bags.filters.tagType'));
    const labelCreateTime = String(t('wms.bags.filters.createTime'));
    const labelStatus = String(t('wms.bags.filters.status'));
    const labelBaggingTaskNumber = String(t('wms.bags.filters.baggingTaskNumber'));
    const labelCarrier = String(t('wms.bags.filters.carrier'));
    const labelSourceType = String(t('wms.bags.filters.sourceType'));
    const labelBatchNumber = String(t('wms.bags.filters.batchNumber'));
    const labelMergedTo = String(t('wms.bags.filters.mergedTo'));
    const labelSortingRuleSecond = String(t('wms.bags.filters.presortCode'));
    const labelSortingCodeSegments = String(t('wms.bags.filters.sortingCodeSegments'));
    const labelSortingCodeSeg1 = String(t('wms.bags.filters.sortingCodeSeg1'));
    const labelSortingCodeSeg2 = String(t('wms.bags.filters.sortingCodeSeg2'));
    const labelSortingCodeSeg3 = String(t('wms.bags.filters.sortingCodeSeg3'));
    const labelSortingCodeSeg4 = String(t('wms.bags.filters.sortingCodeSeg4'));

    const allLabels = [
        labelBagNumber,
        labelTagType,
        labelCreateTime,
        labelStatus,
        labelBaggingTaskNumber,
        labelCarrier,
        labelMergedTo,
        labelSourceType,
        labelBatchNumber,
        labelSortingCodeSegments,
    ];
    const unifiedLabelClassName = getSmartLabelClassName(allLabels);

    // PPMOD 容器候选（filter 下拉）；TEMU 场景固定；数据量小，页面首次加载即缓存
    const { data: containerData } = useQuery<ListActiveBagContainersResponse>(
        LIST_ACTIVE_BAG_CONTAINERS,
        { variables: { operateType: BagContainerOperateType.TEMU } }
    );
    const containerOptions = useMemo(
        () => (containerData?.listActiveBagContainers || []).map(c => ({
            label: c.name,
            value: c.id,
        })),
        [containerData]
    );

    // 直接返回 t() 结果——不使用 `t(...) || '兜底'` 模式：i18next 未命中 key 时会返回 key 字符串本身
    // （truthy），|| 短路不会走到兜底，反而误导下一个读者以为有防御。缺 key 的正确解法是补词条
    // 到 zh/en/es 三份 translation.json（本文件依赖的 wms.bags.externalBusinessType.{normal,ppmod} 三语已补齐）
    const externalBusinessTypeOptions = useMemo(
        () => [
            { value: ExternalBusinessType.NORMAL, label: t('wms.bags.externalBusinessType.normal') },
            { value: ExternalBusinessType.PPMOD, label: t('wms.bags.externalBusinessType.ppmod') },
        ],
        [t]
    );

    const { data: carrierData, loading: carrierLoading } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS);
    const carrierOptions = carrierData?.getAllCarrierOptions.map(carrier => ({
        value: carrier.value,
        label: carrier.name
    })) || [];

    // ============================================================
    // 分拣码分段筛选：seg1 混合展示 SWX Hub + TT sortCenterCode + TEMU GA/SCF；
    // 选中不同类型的 seg1 → seg2/seg3 自适应候选源。
    // ============================================================

    // SWX Hub 列表（seg1 主候选源，seg1 = hub.sortingCode）
    const { data: hubData } = useQuery<GetHubListResponse>(GET_HUB_LIST);
    const swxSeg1Options = useMemo(
        () => (hubData?.hubList || [])
            .filter(h => !!h.sortingCode)
            .map(h => ({ label: `${h.sortingCode} - ${h.name}`, value: h.sortingCode as string, hubId: h.id })),
        [hubData]
    );

    // TT sortCenterCode 映射（seg1 追加源，来自 common_config TIKTOK_SORT_CENTER_HUB_MAPPING，
    // 后端已 join warehouse 拿到 warehouseName）
    const { data: ttMappingData } = useQuery<GetTikTokSortCenterMappingsResponse>(GET_TIKTOK_SORT_CENTER_MAPPINGS);
    const ttSortCenterMappings = useMemo(
        () => ttMappingData?.getTikTokSortCenterMappings || [],
        [ttMappingData]
    );
    // label 展示 "sortCenterCode - warehouseName (TT)"，warehouseName 缺失时省略中间段。
    //
    // 不与 SWX Hub sortingCode 去重：seg1 已改用「来源::字面量」复合 key（buildSeg1Key），
    // 同字面量项的 value 不再冲突，三源可各自并存。此前的去重会把与 hub 分拣码同值的
    // TT sortCenterCode 整项丢掉（如 LAX 既是 hub 分拣码又是 TT 分拣中心码时只剩 hub 那项），
    // 导致用户走不到 TT 分支（seg2 = NEXT_STOP），只能走 hub 分支（seg2 = 仓库列表）。
    const ttSortCenterOptions = useMemo(
        () => ttSortCenterMappings.map(m => ({
            label: m.warehouseName
                ? `${m.sortCenterCode} - ${m.warehouseName} (TT)`
                : `${m.sortCenterCode} (TT)`,
            value: m.sortCenterCode,
        })),
        [ttSortCenterMappings]
    );

    // 混合 seg1：SWX Hub + TT sortCenterCode + TEMU GA/SCF/LA
    // 三源同字面量的项（如 hub "LA" 与 TEMU DHLe "LA"、hub "LAX" 与 TT "LAX"）语义不同、
    // 需并存展示，故 value 带来源前缀；三源均不做跨源去重，各自的全部候选都会出现在 seg1 里。
    const seg1Options = useMemo(() => [
        ...swxSeg1Options.map(o => ({ label: o.label, value: buildSeg1Key('hub', o.value) })),
        ...ttSortCenterOptions.map(o => ({ label: o.label, value: buildSeg1Key('tt', o.value) })),
        ...TEMU_SEG1_OPTIONS.map(o => ({ label: `${o.label} (TEMU)`, value: buildSeg1Key('temu', o.value) })),
    ], [swxSeg1Options, ttSortCenterOptions]);

    // seg1 选中的是 SWX Hub / TT sortCenter / TEMU —— 直接取自复合 key 里的来源，
    // 不再用"字面量落在哪个候选源"反推（同值时反推必然歧义）
    const seg1Selected = parseSeg1Key(filters.sortingCodeSegment1Key);
    const seg1Branch: Seg1Source | null = seg1Selected?.source ?? null;
    const selectedHubId = seg1Branch === 'hub'
        ? swxSeg1Options.find(o => o.value === seg1Selected?.value)?.hubId
        : undefined;
    const temuSelectedSeg1 = seg1Branch === 'temu'
        ? TEMU_SEG1_OPTIONS.find(o => o.value === seg1Selected?.value)
        : undefined;
    // DHLe（stationType=3）为单段分拣码：seg1 即目的地 "LA"，无 seg2/seg3 候选。
    // 定义在此处（而非下方 seg2/seg3 分派处）是为了让 seg3 候选查询也能据此 skip，避免发无用请求。
    const isTemuDhle = temuSelectedSeg1?.stationType === 3;

    // Hub 分支：seg2 = 该 Hub 下 Warehouse 列表
    const { data: warehouseData } = useQuery<GetWarehouseListByHubIdResponse>(GET_WAREHOUSE_LIST_BY_HUB_ID, {
        variables: { hubId: selectedHubId },
        skip: seg1Branch !== 'hub' || !selectedHubId,
    });
    const hubSeg2Options = useMemo(
        () => (warehouseData?.warehouseList || [])
            .filter(w => !!w.sortingCode)
            .map(w => {
                const parts = (w.sortingCode as string).split('-');
                return { label: `${parts[1] ?? w.sortingCode} - ${w.name}`, value: parts[1] ?? (w.sortingCode as string), warehouseId: w.id };
            }),
        [warehouseData]
    );
    const selectedWarehouseId = hubSeg2Options.find(o => o.value === filters.sortingCodeSegment2)?.warehouseId;

    // Hub 分支：seg3 = 路区（Route）——参考 NewBagForm，从 area.sortingCode 第 3 段抽字母部分并 distinct
    // 例：sortingCode "LAX-DTLA-A123" → 路区 "A"；同一路区下多个 area 只展示一次
    const { data: areaData } = useQuery<GetAreasByWarehouseIdResponse>(GET_AREAS_BY_WAREHOUSE_ID, {
        variables: { warehouseId: selectedWarehouseId },
        skip: seg1Branch !== 'hub' || !selectedWarehouseId,
    });
    const hubSeg3Options = useMemo(() => {
        const letters = new Set<string>();
        (areaData?.areaList || []).forEach(a => {
            if (!a.sortingCode) return;
            const parts = (a.sortingCode as string).split('-');
            const letter = parts[2]?.match(/^[A-Za-z]+/)?.[0];
            if (letter) letters.add(letter);
        });
        return Array.from(letters).sort().map(letter => ({ label: letter, value: letter }));
    }, [areaData]);

    // TEMU 分支：seg3 = getExternalStationSortingCodes(stationType, carrierCode)（destination code 列表）
    //
    // gql 契约要求 carrierCode 必填。未选尾程商时**不再 skip**，而是用 [USPS_CARRIER_CODE] 兜候选 ——
    // 否则 seg3 下拉会空着且界面无提示（线上反馈的问题）。筛选本身不按 carrier 匹配，
    // 故此默认值只影响「能选到哪些码」，不影响筛出来的结果；详见 USPS_CARRIER_CODE 的说明。
    // 用 `??` 而非 `||`：SWIFTX 的 carrierCode 是 0，用 `||` 会把它误判为未选。
    //
    // 残留限制：未选尾程商时候选仅为 USPS 的 destination code，其它尾程商独有的码选不到。
    // 要让候选完整需后端支持 carrierCode 可空（并合并 TEMU_HANDOVER(3)），见 docs/plans。
    const { data: temuSortingCodesData } = useQuery<GetExternalStationSortingCodesResponse>(
        GET_EXTERNAL_STATION_SORTING_CODES,
        {
            variables: {
                stationType: temuSelectedSeg1?.stationType,
                carrierCode: filters.carrierCode ?? USPS_CARRIER_CODE,
            },
            // DHLe 无 seg3 候选（下方 seg3Options 恒为 []），跳过以免发无用请求
            skip: seg1Branch !== 'temu' || !temuSelectedSeg1 || isTemuDhle,
        }
    );
    const temuSeg3Options = useMemo(
        () => (temuSortingCodesData?.getExternalStationSortingCodes || []).map(code => ({
            label: code,
            value: code,
        })),
        [temuSortingCodesData]
    );

    // TT 分支候选：
    //   seg2 (nextStop) 来自 tt_location_config 表 NEXT_STOP 类型（走 getTtLocationOptions(3)），与 NewBagForm/PDA 装包扫描下一站选项同源；
    //   seg3 (carrierAlias) 由 carrierOptions 经 ttBaggingSortingRuleCarrierSegment 映射得到的三位缩写（MIX/无缩写略去），
    //   与后端 Bag.computeSortingRule 拼出的字符串一致
    const { data: ttNextStopData } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
        variables: { locationType: TT_LOCATION_TYPE.NEXT_STOP },
        skip: seg1Branch !== 'tt',
    });
    const ttSeg2Options = useMemo<Array<{ label: string; value: string }>>(() => {
        return (ttNextStopData?.getTtLocationOptions || []).map(loc => ({
            label: loc.name ? `${loc.code} - ${loc.name}` : loc.code,
            value: loc.code,
        }));
    }, [ttNextStopData]);
    const ttSeg3Options = useMemo<Array<{ label: string; value: string }>>(() => {
        const seen = new Set<string>();
        const result: Array<{ label: string; value: string }> = [];
        (carrierData?.getAllCarrierOptions || []).forEach(c => {
            const alias = ttBaggingSortingRuleCarrierSegment(c.value);
            if (alias && !seen.has(alias)) {
                seen.add(alias);
                result.push({ label: `${alias} - ${c.name}`, value: alias });
            }
        });
        return result;
    }, [carrierData]);

    // 对外的 seg2/seg3 候选（按 seg1 选中的分支切换；未选 seg1 时空）
    // DHLe 无 seg2/seg3 候选 —— isTemuDhle 定义见上方 temuSelectedSeg1 处
    type SegOption = { label: string; value: string };
    const seg2Options: SegOption[] = seg1Branch === 'temu'
        ? (isTemuDhle ? [] : TEMU_SEG2_OPTIONS)
        : seg1Branch === 'hub'
            ? hubSeg2Options.map(o => ({ label: o.label, value: o.value }))
            : seg1Branch === 'tt'
                ? ttSeg2Options
                : [];
    const seg3Options: SegOption[] = seg1Branch === 'temu'
        ? (isTemuDhle ? [] : temuSeg3Options)
        : seg1Branch === 'hub'
            ? hubSeg3Options
            : seg1Branch === 'tt'
                ? ttSeg3Options
                : [];

    // Apply filters
    const handleChange = (key: keyof FilterOptions, value: string | 'all') => {
        setFilters({ ...filters, [key]: value } as FilterOptions);
    };

    /*const warehouses = useWarehouseStore(state => state.warehouses);
    const bagCreatedAtOptions = [
        {value: 'all', label: 'All'},
         ...warehouses.filter(warehouse => warehouse.siteType === SiteType.WAREHOUSE).map(warehouse => ({
            value: warehouse.id,
            label: warehouse.name
        }))
    ];*/

    // 使用国际化版本
    const localizedBagTypeOptions = getBagTypeOptions(t);
    const localizedBagStatusOptions = getBagStatusOptions(t);
    const localizedSourceTypeOptions = getSourceTypeOptions(t);

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelBagNumber}
                        value={filters.bagNumber}
                        onChange={(e) => handleChange('bagNumber', e.target.value)}
                        placeholder={t('wms.bags.placeholders.bagNumber') || ''}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelExternalBagNumber}
                        value={filters.externalBagNumber}
                        onChange={(e) => handleChange('externalBagNumber', e.target.value)}
                        placeholder={t('wms.bags.placeholders.externalBagNumber') || ''}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelTagType}
                        options={localizedBagTypeOptions}
                        value={filters.bagType}
                        onChange={(value: string) => handleChange('bagType', (value || 'all') as any)}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelCreateTime}
                        value={createdAtRange}
                        onChange={(dates) => {
                            const dayjsRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] = dates
                                ? [
                                    dates[0] ? dayjs(dates[0], 'YYYY/MM/DD') : null,
                                    dates[1] ? dayjs(dates[1], 'YYYY/MM/DD') : null
                                ]
                                : [null, null];
                            setFilters({ ...filters, createdAtRange: dayjsRange });
                        }}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelStatus}
                        options={localizedBagStatusOptions}
                        value={filters.status}
                        onChange={(value: string) => handleChange('status', value || 'all')}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelBaggingTaskNumber}
                        value={filters.bagTaskNumber}
                        onChange={(e) => handleChange('bagTaskNumber', e.target.value)}
                        placeholder=""
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelCarrier}
                        options={carrierOptions}
                        value={filters.carrierCode !== undefined ? String(filters.carrierCode) : undefined}
                        onChange={(value: string | undefined) => {
                            const numValue = value !== undefined && value !== null && value !== '' 
                                ? Number(value) 
                                : undefined;
                            setFilters({ ...filters, carrierCode: numValue });
                        }}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                        loading={carrierLoading}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelSourceType}
                        options={localizedSourceTypeOptions}
                        value={
                            filters.sourceType !== undefined &&
                            filters.sourceType !== 'all' &&
                            Number.isFinite(Number(filters.sourceType))
                                ? String(filters.sourceType)
                                : 'all'
                        }
                        onChange={(value: string | undefined) => {
                            if (value === undefined || value === null || value === '' || value === 'all') {
                                setFilters({ ...filters, sourceType: 'all' });
                            } else {
                                const num = Number(value);
                                setFilters({
                                    ...filters,
                                    sourceType: Number.isFinite(num) ? num : 'all'
                                });
                            }
                        }}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelBatchNumber}
                        value={filters.batchNumber}
                        onChange={(e) => setFilters({ ...filters, batchNumber: e.target.value })}
                        placeholder={t('wms.bags.placeholders.batchNumber') || ''}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelMergedTo}
                        value={filters.mergedToBagNumber || ''}
                        onChange={(e) => setFilters({ ...filters, mergedToBagNumber: e.target.value })}
                        placeholder={t('wms.bags.placeholders.mergedTo') || ''}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelSortingRuleSecond}
                        value={filters.sortingRuleSecond || ''}
                        onChange={(e) => setFilters({ ...filters, sortingRuleSecond: e.target.value })}
                        placeholder={t('wms.bags.placeholders.presortCode') || ''}
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                {/* PPMOD 筛选：外部业务类型（普通/PP）+ 容器类型（bag_container 字典） */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={String(t('wms.bags.filters.externalBusinessType') || 'External Business Type')}
                        options={externalBusinessTypeOptions.map(o => ({ label: o.label, value: String(o.value) }))}
                        value={filters.externalBusinessType !== undefined ? String(filters.externalBusinessType) : undefined}
                        onChange={(value: string | undefined) => {
                            const num = value !== undefined && value !== null && value !== '' ? Number(value) : undefined;
                            setFilters({ ...filters, externalBusinessType: Number.isFinite(num) ? num : undefined });
                        }}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={String(t('wms.bags.filters.containerType') || 'Container Type')}
                        options={containerOptions.map(o => ({ label: o.label, value: String(o.value) }))}
                        value={filters.bagContainerId !== undefined ? String(filters.bagContainerId) : undefined}
                        onChange={(value: string | undefined) => {
                            const num = value !== undefined && value !== null && value !== '' ? Number(value) : undefined;
                            setFilters({ ...filters, bagContainerId: Number.isFinite(num) ? num : undefined });
                        }}
                        className="w-full"
                        spanClassName={unifiedLabelClassName}
                    />
                </div>
                {/* 分拣码分段筛选：前 3 段级联下拉（支持搜索），第 4 段自由输入；4 段拼成前缀交后端 LIKE 匹配 */}
                <div className="min-w-0 col-span-3">
                    <div className="flex items-start gap-2">
                        <span className={`${unifiedLabelClassName} pt-1 shrink-0`}>{labelSortingCodeSegments}</span>
                        <div className="grid grid-cols-4 gap-2 flex-1 min-w-0">
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder={labelSortingCodeSeg1}
                                value={filters.sortingCodeSegment1Key}
                                options={seg1Options}
                                onChange={(key?: string) => setFilters({
                                    ...filters,
                                    sortingCodeSegment1Key: key,
                                    sortingCodeSegment1: parseSeg1Key(key)?.value,
                                    sortingCodeSegment2: undefined,
                                    sortingCodeSegment3: undefined,
                                    sortingCodeSegment4: undefined,
                                })}
                                className="w-full"
                            />
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder={labelSortingCodeSeg2}
                                value={filters.sortingCodeSegment2}
                                options={seg2Options}
                                disabled={!filters.sortingCodeSegment1}
                                onChange={(value?: string) => setFilters({
                                    ...filters,
                                    sortingCodeSegment2: value,
                                    sortingCodeSegment3: undefined,
                                    sortingCodeSegment4: undefined,
                                })}
                                className="w-full"
                            />
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder={labelSortingCodeSeg3}
                                value={filters.sortingCodeSegment3}
                                options={seg3Options}
                                disabled={!filters.sortingCodeSegment2}
                                onChange={(value?: string) => setFilters({
                                    ...filters,
                                    sortingCodeSegment3: value,
                                    sortingCodeSegment4: undefined,
                                })}
                                className="w-full"
                            />
                            <Input
                                allowClear
                                placeholder={labelSortingCodeSeg4}
                                value={filters.sortingCodeSegment4 || ''}
                                onChange={(e) => setFilters({ ...filters, sortingCodeSegment4: e.target.value })}
                                disabled={!filters.sortingCodeSegment3}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterArea; 